/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import { ActivityPart } from '../../activity-feed/activity-view';
import {
  ItemRoute, itemCommentCount, itemLikeCount, itemRoute, itemTypeLabel,
} from '../../common/community/feed/community-item';
import { toEmojiText } from '../../common/community/feed/preview-html';

// Turns the profile's Forum posts, Comments, Mentions and Likes feeds into feed-style cards.

export type CommunityProfileActivityKind = 'comments' | 'mentions' | 'likes';

export interface CommunityProfileCard {
  // Why the card is on this tab ("Commented on …"), above the author.
  context?: { icon: string; parts: ActivityPart[] };
  // When the context happened (a like's time). The meta line keeps the content's own date.
  contextDate?: string;
  author: any;
  isYou: boolean;
  type: string;
  datePrefix?: string;
  date: string;
  title?: string;
  // Plain text, clamped to three lines, or eight when it is a status's text.
  text?: string;
  isStatus?: boolean;
  route: ItemRoute | null;
  viewLabel: string;
  stat?: string;
  // Forum posts only: the post's category, linked above its title.
  category?: { id: number; name: string; parent?: { id: number; name: string } };
  // Forum posts only: the post the card's like action and likers list act on.
  likeable?: any;
  // Names the card for screen readers.
  label: string;
}

export interface CardOptions {
  // The profile owner, and whether that's the signed-in user.
  owner: any;
  isOwn: boolean;
  // Markdown or HTML to preview HTML: links and emoji only.
  toText: (content: string) => string;
}

// The same labels as CommentService.getCommentableLabel.
function subjectLabel(item: any): string {
  if (item.type === 'status') return 'a status';
  return item.title ?? item.name ?? '';
}

function subject(item: any): ActivityPart {
  const route = itemRoute(item);
  return route ? { text: subjectLabel(item), ...route } : { text: subjectLabel(item) };
}

function count(n: number, one: string, many: string): string | undefined {
  if (!n) return undefined;
  return `${n} ${n === 1 ? one : many}`;
}

function viewLabel(item: any): string {
  return item.type === 'topic' ? 'View discussion' : `View ${itemTypeLabel(item).toLowerCase()}`;
}

function build(card: Omit<CommunityProfileCard, 'isYou' | 'label'>, options: CardOptions): CommunityProfileCard {
  const isYou = options.isOwn && card.author?.id === options.owner.id;
  return { ...card, isYou, label: `${card.type} by ${card.author?.name}${card.title ? `: ${toEmojiText(card.title)}` : ''}` };
}

export function toTopicCard(topic: any, options: CardOptions): CommunityProfileCard {
  return build({
    author: topic.user || options.owner,
    type: 'Forum post',
    datePrefix: 'last reply ',
    date: topic.last_commented_at || topic.published_at,
    title: topic.title,
    text: topic.content ? options.toText(topic.content) : undefined,
    category: topic.category,
    route: itemRoute(topic),
    viewLabel: 'View discussion',
    likeable: topic,
    stat: count(itemCommentCount(topic), 'reply', 'replies'),
  }, options);
}

export function toCommunityProfileActivityCard(kind: CommunityProfileActivityKind, entry: any, options: CardOptions): CommunityProfileCard | null {
  switch (kind) {
    case 'comments':
      return entry.commentable ? build({
        context: { icon: 'chat_bubble_outline', parts: [{ text: 'Commented on ' }, subject(entry.commentable)] },
        author: entry.user,
        type: 'Comment',
        date: entry.published_at,
        text: options.toText(entry.content),
        route: itemRoute(entry.commentable),
        viewLabel: 'View comment',
      }, options) : null;
    case 'mentions': {
      const item = entry.item?.commentable || entry.item;
      if (!item) return null;
      const who = options.isOwn ? 'you' : options.owner.name;
      return build({
        context: { icon: 'alternate_email', parts: [{ text: `Mentioned ${who} in ` }, subject(item)] },
        author: entry.user,
        type: itemTypeLabel(entry.item),
        date: entry.date,
        text: options.toText(entry.item.content),
        isStatus: entry.item.type === 'status',
        route: itemRoute(item),
        viewLabel: viewLabel(item),
      }, options);
    }
    case 'likes': {
      const item = entry.item?.commentable || entry.item;
      if (!item) return null;
      // The liked post's author when the API sends it; otherwise the activity's user, as the legacy page did.
      return build({
        context: { icon: 'favorite_border', parts: [{ text: 'You liked this ' }] },
        contextDate: entry.date,
        author: entry.item.user || entry.user,
        type: itemTypeLabel(entry.item),
        date: entry.item.published_at || entry.date,
        title: entry.item.title,
        text: options.toText(entry.item.content),
        isStatus: entry.item.type === 'status',
        route: itemRoute(item),
        viewLabel: viewLabel(item),
        stat: count(itemLikeCount(entry.item), 'like', 'likes'),
      }, options);
    }
    default:
      return null;
  }
}
